import { useActionState, useState, useTransition } from "react"
import { useDebounceCallback } from "usehooks-ts"

import type { ActionResponse } from "@/utils/server/withActionContext"

import { useToast } from "./use-toast"

export const useServerAction = <T, R = undefined, M = undefined>(
  action: (data: T) => Promise<ActionResponse<R, M>>,
  { debounceTime = 2000, showToast = true }: { debounceTime?: number; showToast?: boolean } = {}
) => {
  const { toast } = useToast()
  const [, startTransition] = useTransition()

  const [response, setResponse] = useState<ActionResponse<R, M> | null>(null)
  const [isPending, setIsPending] = useState(false)

  const [, _serverAction] = useActionState(async (_: unknown, data: T) => {
    const _response = await handleServerAction(data)
    setResponse(_response)
    setIsPending(false)
    return _response
  }, null)

  const handleServerAction = async (data: T) => {
    const _response = await action(data)

    if (showToast && _response && !_response.success && _response.errors.length > 0) {
      toast({
        title: _response?.errors[0],
        variant: "destructive",
      })
    }
    return _response
  }

  const serverActionSync = (data: T, callback?: (response: ActionResponse<R, M>) => void) => {
    setIsPending(true)
    startTransition(async () => {
      const _response = await handleServerAction(data)
      setIsPending(false)
      setResponse(_response)
      if (callback && _response) callback(_response)
    })
  }

  const serverAction = (data: T) => {
    setIsPending(true)
    startTransition(() => _serverAction(data))
  }
  const debouncedServerAction = useDebounceCallback(serverAction, debounceTime)
  const reset = () => {
    setResponse(null)
    setIsPending(false)
  }

  return { response, serverAction, debouncedServerAction, isPending, serverActionSync, reset }
}
